import React from 'react'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'
import StepperButton from './StepperButton'
import {IncomeDocumentStateTypes, IncomeDocumentTypes} from '../../../constants/Common'
import {useNavigate} from 'react-router-dom'
import {filter, find} from 'lodash'
import Dropdown from 'react-bootstrap/Dropdown'
import {Button} from 'react-bootstrap'
import {useAuth} from '../../../modules/auth'
import api from '../../../utils/Api'

interface IIncomeDocumentStepper {
  document: IIncomeDocument
  // หน้าที่แสดง stepper เป็นคนถือ modal รับชำระกับการรีเฟรชเอกสารเอง
  // ตัว stepper แค่บอกว่าปุ่มถูกกด ไม่ต้องรู้เรื่องเงื่อนไข state หรือสิทธิ์
  // ไม่ส่ง prop นี้มาแปลว่ารับชำระจากหน้านี้ไม่ได้ ปุ่มจะกดไม่ติดเหมือนเดิม
  onReceivePayment?(): void
}

const IncomeDocumentStepper: React.FC<IIncomeDocumentStepper> = (props) => {
  const { document, onReceivePayment } = props
  const navigate = useNavigate()
  const {can} = useAuth()

  if (!document) return <></>

  const isVoided = document.state === 'voided'
  const isInitialDocument = !document.linkingDocumentId
  const childrenDocuments = document?.childrenDocuments || []
  const btnClass = 'btn-income-document-stepper'

  const _renderQuotationStepper = () => {
    const isAccepted = ['accepted'].includes(document.state)
    const relateInvoiceDocument = find(childrenDocuments, {type: 'invoice'})
    const relateInvoicePayments = relateInvoiceDocument?.incomeDocumentPayments || []
    const relateInvoiceReceiptDocuments = relateInvoiceDocument?.childrenDocuments?.filter((doc: any) => doc.type === 'receipt')

    const taxInvoice = find(relateInvoiceDocument?.childrenDocuments, {type: 'tax-invoice'})
    const taxReceipt = find(relateInvoiceReceiptDocuments?.childrenDocuments, {type: 'tax-invoice'})

    return (
      <>
        <StepperButton
          title={'ใบเสนอราคา'}
          state={'passed'}
        />
        <StepperButton
          title={'ตอบรับแล้ว'}
          state={isVoided ? 'disabled' : (isAccepted ? 'passed' : 'pending')}
        />
        <StepperButton
          title={'ใบแจ้งหนี้'}
          state={isAccepted ? relateInvoiceDocument ? 'passed' : 'pending' : 'disabled'}
          onClick={() => {
            if (relateInvoiceDocument) {
              navigate(`/invoice/${relateInvoiceDocument.id}/detail`)
              return
            }

            navigate(`/invoice/create?docId=${document.id}`)
          }}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={isAccepted
            ? (relateInvoiceDocument ? (relateInvoiceDocument?.state === 'paid' ? 'passed' : 'pending') : 'disabled')
            : 'disabled'}
        />
        <StepperButton
          title={'ใบเสร็จรับเงิน'}
          state={isAccepted
            ? (relateInvoiceReceiptDocuments?.length
              ? 'passed'
              : (relateInvoiceDocument?.state === 'paid' ? 'pending' : 'disabled')
            ) : 'disabled'}
          onClick={() => {
            if (!relateInvoiceReceiptDocuments) return
            if (relateInvoiceReceiptDocuments?.length === 1) {
              navigate(`/revenue-receipt/${relateInvoiceReceiptDocuments?.[0]?.id}/detail`)
            }
          }}
        />
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={taxInvoice || taxReceipt ? 'passed' : 'disabled'}
          onClick={() => {
            if (taxInvoice?.linkingDocumentId) {
              navigate(`/invoice/${taxInvoice?.linkingDocumentId}/detail`)
              return
            }

            if (taxReceipt?.linkingDocumentId) {
              navigate(`/revenue-receipt/${taxReceipt?.linkingDocumentId}/detail`)
              return
            }
          }}
        />
      </>
    )
  }

  const _renderDepositStepper = () => {
    const taxDocument = find(document.childrenDocuments, {type: 'tax-invoice'})
    const quotationDocument = document?.linkingDocument?.type === 'quotation' ? document.linkingDocument : undefined

    return (
      <>
        {quotationDocument && (
          <>
            <StepperButton
              title={'ใบเสนอราคา'}
              state={'passed'}
              onClick={() => {
                navigate(`/quotation/${quotationDocument.id}/detail`)
              }}
            />
            <StepperButton
              title={'ตอบรับแล้ว'}
              state={'passed'}
            />
          </>
        )}
        <StepperButton
          title={'ใบรับเงินมัดจำ'}
          state={'passed'}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={['await-deduct', 'deducted'].includes(document.state)
            ? 'passed'
            : (['outstanding', 'overdue'].includes(document.state) ? 'pending' : 'disabled')}
        />
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={taxDocument ? 'passed' : 'disabled'}
        />
      </>
    )
  }

  const _renderInvoiceStepper = () => {
    const taxDocument = find(document.childrenDocuments, {type: 'tax-invoice'})
    const receiptDocuments = document.childrenDocuments.filter(v => v.type === 'receipt')
    const quotationDocument = document?.linkingDocument?.type === 'quotation' ? document.linkingDocument : undefined
    const payments = document?.incomeDocumentPayments
    // const currentBillingHistory = find(document?.billingHistories, {originalDocumentId: document.id})
    // const billingHistoryReceiptDocuments = filter(currentBillingHistory?.incomeDocument?.childrenDocuments, {type: 'receipt'})

    const billingHistoryDocuments = filter(document?.billingHistories, {originalDocumentId: document.id})
    const billingHistoryReceiptDocuments = billingHistoryDocuments?.map((doc) => {
      return filter(doc?.incomeDocument?.childrenDocuments, {type: 'receipt'})
    }).flat()

    const billingPayments = billingHistoryDocuments?.map((doc) => {
      return doc?.incomeDocument?.incomeDocumentPayments
    }).flat()

    const allPayments = [
      ...payments,
      ...billingPayments,
    ]

    const allReceiptDocuments = [
      ...receiptDocuments,
      ...billingHistoryReceiptDocuments
    ]

    return (
      <>
        {quotationDocument && (
          <>
            <StepperButton
              title={'ใบเสนอราคา'}
              state={'passed'}
              onClick={() => {
                navigate(`/quotation/${quotationDocument.id}/detail`)
              }}
            />
            <StepperButton
              title={'ตอบรับแล้ว'}
              state={'passed'}
            />
          </>
        )}
        <StepperButton
          title={'ใบแจ้งหนี้'}
          state={'passed'}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={document?.state === 'paid' ? 'passed' : 'pending'}
          counter={allPayments?.length}
          onClick={onReceivePayment}
        />
        <StepperButton
          title={'ใบเสร็จรับเงิน'}
          state={(allReceiptDocuments.length) ? 'passed' : 'disabled'}
          counter={allReceiptDocuments.length}
          documents={allReceiptDocuments}
          onClick={() => {
            if (!allReceiptDocuments.length) return
            if (allReceiptDocuments.length === 1) {
              navigate(`/revenue-receipt/${allReceiptDocuments[0].id}/detail`)
              return
            }
          }}
        />
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={taxDocument ? 'passed' : 'disabled'}
        />
      </>
    )
  }

  const _renderReceiptStepper = () => {
    const taxDocument = find(document.childrenDocuments, {type: 'tax-invoice'})
    const invoiceDocument = document?.linkingDocument?.type === 'invoice' ? document.linkingDocument : undefined
    const invoiceTaxDocument = find(invoiceDocument?.childrenDocuments, {type: 'tax-invoice'})
    const quotationDocument = invoiceDocument?.linkingDocument?.type === 'quotation' ? invoiceDocument.linkingDocument : undefined

    return (
      <>
        {quotationDocument && (
          <>
            <StepperButton
              title={'ใบเสนอราคา'}
              state={'passed'}
              onClick={() => {
                navigate(`/quotation/${quotationDocument.id}/detail`)
              }}
            />
            <StepperButton
              title={'ตอบรับแล้ว'}
              state={'passed'}
            />
          </>
        )}
        {invoiceDocument && (
          <StepperButton
            title={'ใบแจ้งหนี้'}
            state={'passed'}
            onClick={() => {
              navigate(`/invoice/${invoiceDocument.id}/detail`)
            }}
          />
        )}
        <StepperButton
          title={'ใบเสร็จรับเงิน'}
          state={'passed'}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={document.state === 'paid' ? 'passed' : 'disabled'}
        />
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={taxDocument || invoiceTaxDocument ? 'passed' : 'disabled'}
          onClick={() => {
            if (invoiceTaxDocument) {
              navigate(`/invoice/${invoiceDocument.id}/detail`)
            }
          }}
        />
      </>
    )
  }

  const _renderTaxInvoiceStepper = () => {
    return (
      <>
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={'passed'}
        />
      </>
    )
  }

  const _renderCreditDebitStepper = () => {
    const invoiceDocument = document?.linkingDocument?.type === 'invoice' ? document?.linkingDocument : undefined
    const receiptDocument = document?.linkingDocument?.type === 'receipt' ? document?.linkingDocument : undefined

    return (
      <>
        {invoiceDocument && (
          <StepperButton
            title={'ใบแจ้งหนี้'}
            state={'passed'}
            onClick={() => {
              navigate(`/invoice/${invoiceDocument.id}/detail`)
            }}
          />
        )}
        {receiptDocument && (
          <StepperButton
            title={'ใบเสร็จรับเงิน'}
            state={'passed'}
            onClick={() => {
              navigate(`/revenue-receipt/${receiptDocument.id}/detail`)
            }}
          />
        )}
        {(invoiceDocument?.issueTaxInvoice || receiptDocument?.issueTaxInvoice) && (
          <StepperButton
            title={'ออกใบกำกับภาษี'}
            state={'passed'}
          />
        )}
        <StepperButton
          title={'ใบลดหนี้'}
          state={'passed'}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={document.state === 'paid' ? 'passed' : 'pending'}
        />
      </>
    )
  }

  const _renderBillingStepper = () => {
    const receiptDocument = find(document.childrenDocuments, {type: 'receipt'})
    const taxReceipt = receiptDocument?.linkingDocument?.type === 'tax-invoice' ? receiptDocument?.linkingDocument : undefined

    return (
      <>
        <StepperButton
          title={'ใบวางบิล'}
          state={'passed'}
        />
        <StepperButton
          title={'รับชำระเงิน'}
          state={document.state === 'paid' ? 'passed' : 'pending'}
        />
        <StepperButton
          title={'ใบเสร็จรับเงิน'}
          state={receiptDocument ? 'passed' : 'disabled'}
          onClick={() => {
            if (!receiptDocument) return

            navigate(`/revenue-receipt/${receiptDocument.id}/detail`)
          }}
        />
        <StepperButton
          title={'ออกใบกำกับภาษี'}
          state={taxReceipt ? 'passed' : 'disabled'}
        />
      </>
    )
  }

  const _renderExpenseRequestPurchaseOrder = () => {
    const childrenDocs = document.childrenDocuments
    const isApproved = [
      IncomeDocumentStateTypes.APPROVED,
      IncomeDocumentStateTypes.PARTIAL,
      IncomeDocumentStateTypes.COMPLETELY,
    ].includes(document.state as IncomeDocumentStateTypes)
    const canCreatePurchaseOrder =
      !isVoided &&
      [IncomeDocumentStateTypes.APPROVED, IncomeDocumentStateTypes.PARTIAL].includes(
        document.state as IncomeDocumentStateTypes
      ) &&
      can('accounting_purchase_order_create')

    const purchaseOrderState = () => {
      if (childrenDocs.length) return 'passed'
      return canCreatePurchaseOrder ? 'pending' : 'disabled'
    }

    return (
      <>
        <StepperButton
          title={'ใบขอสั่งซื้อ'}
          state={'passed'}
        />
        <StepperButton
          title={'อนุมัติแล้ว'}
          state={isApproved ? 'passed' : 'disabled'}
        />
        <StepperButton
          title={'ใบสั่งซื้อ'}
          state={purchaseOrderState()}
          counter={childrenDocs.length}
          documents={childrenDocs}
          onClick={() => {
            if (!childrenDocs.length) {
              if (!canCreatePurchaseOrder) return
              navigate(`/expense-purchase-order/create?docId=${document.id}`)
              return
            }
            if (childrenDocs.length > 1) return
            navigate(`/expense-purchase-order/${childrenDocs[0].id}/detail`)
          }}
        />
      </>
    )
  }

  const _renderExpensePurchaseOrder = () => {
    const purchaseItems = document.incomeDocumentItems || []
    // นับเฉพาะ deduction ที่ยังผูกกับบันทึกซื้อที่มีอยู่จริง (ถ้าบันทึกซื้อถูกลบ ref จะเป็น null)
    // ให้ตรงกับกติกาที่ API ใช้คิดสถานะเอกสาร จะได้ไม่แสดงว่าครบแล้วทั้งที่ยังบันทึกซื้อได้อีก
    const deductionItems = (document.deductionItems || []).filter(
      (item: any) => !!item?.incomeDocumentRefId
    )

    // จำนวนที่ถูกนำไปบันทึกซื้อไปแล้วของแต่ละรายการ
    const deductedQuantities = deductionItems.reduce((acc: {[itemId: number]: number}, item: any) => {
      if (!item?.incomeDocumentItemId) return acc

      acc[item.incomeDocumentItemId] = (acc[item.incomeDocumentItemId] || 0) + (item.quantity || 0)

      return acc
    }, {})

    // เงื่อนไขเดียวกับ API check-creating-purchase-record คือยังมีรายการที่บันทึกซื้อไม่ครบจำนวน
    const hasRemainingItems = purchaseItems.some(
      (item: any) => (deductedQuantities[item.id] || 0) < item.quantity
    )
    const isIssuableDocument =
      !isVoided &&
      [
        IncomeDocumentStateTypes.APPROVED,
        IncomeDocumentStateTypes.PARTIAL,
        IncomeDocumentStateTypes.COMPLETELY,
      ].includes(document.state as IncomeDocumentStateTypes)
    // ถือว่าบันทึกซื้อครบแล้วเมื่อเอกสารปิดแล้ว หรือทุกรายการถูกบันทึกซื้อครบจำนวน
    const isCompletely =
      document.state === IncomeDocumentStateTypes.COMPLETELY ||
      (isIssuableDocument && !hasRemainingItems && !!deductionItems.length)
    const canCreatePurchaseRecord =
      isIssuableDocument &&
      !isCompletely &&
      hasRemainingItems &&
      can('accounting_purchase_record_create')

    const createPurchaseRecord = () => {
      api.request(
        `account/income-document/${document.id}/check-creating-purchase-record`,
        (res: any, success: boolean) => {
          if (success && res?.message?.toLowerCase().includes('ok')) {
            navigate(`/expense-purchase-inventory-record/${document.id}/edit?fromOrder=true`)
            return
          }

          // API อัปเดตสถานะเป็น "ออกครบแล้ว" ให้แล้ว โหลดเอกสารใหม่เพื่อให้ stepper ตรงกับสถานะจริง
          const errorMessage = res?.response?.data?.message || ''
          if (errorMessage.includes('ครบแล้ว')) {
            window.location.reload()
          }
        },
        {},
        'POST'
      )
    }

    const purchaseRecordState = () => {
      if (isCompletely) return 'passed'
      return canCreatePurchaseRecord ? 'pending' : 'disabled'
    }

    return (
      <>
        {document?.linkingDocument && (
          <StepperButton
            title={'ใบขอสั่งซื้อ'}
            state={'passed'}
            onClick={() => {
              navigate(`/expense-request-purchase-order/${document?.linkingDocumentId}/detail`)
            }}
          />
        )}
        <StepperButton
          title={'ใบสั่งซื้อ'}
          state={'passed'}
        />
        <StepperButton
          title={'บันทึกซื้อสินค้า'}
          state={purchaseRecordState()}
          onClick={() => {
            if (!canCreatePurchaseRecord) return

            createPurchaseRecord()
          }}
        />
        <StepperButton
          title={'ชำระเงิน'}
          state={'disabled'}
        />
      </>
    )
  }

  return (
    <div className={'d-flex gap-3 mb-4 flex-wrap'}>
      {document.type === IncomeDocumentTypes.QUOTATION && _renderQuotationStepper()}
      {document.type === IncomeDocumentTypes.DEPOSIT && _renderDepositStepper()}
      {document.type === IncomeDocumentTypes.INVOICE && _renderInvoiceStepper()}
      {document.type === IncomeDocumentTypes.RECEIPT && _renderReceiptStepper()}
      {document.type === IncomeDocumentTypes.TAX_INVOICE && _renderTaxInvoiceStepper()}
      {document.type === IncomeDocumentTypes.CREDIT && _renderCreditDebitStepper()}
      {document.type === IncomeDocumentTypes.DEBIT && _renderCreditDebitStepper()}
      {document.type === IncomeDocumentTypes.BILLING && _renderBillingStepper()}
      {document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER && _renderExpensePurchaseOrder()}
      {document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER && _renderExpenseRequestPurchaseOrder()}
    </div>
  )
}

export default IncomeDocumentStepper
